/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useRouter } from 'js/lib/useRouter';

import { Button, Grid, Link, Typography2 } from '@coursera/cds-core';

import { useCourseData } from 'bundles/course-cards/hooks/useCourseData';
import type { ProductStub } from 'bundles/enterprise-legacy-learner-home/types/Products';
import { removeCredentialFromBadgeTitle } from 'bundles/enterprise-legacy-learner-home/utils/EnterpriseBadgeUtils';
import type { EnterpriseBadge } from 'bundles/page-config-common/providers/enterprise-skills/EnterpriseBadgeCollectionsProvider';
import { TabName } from 'bundles/unified-common/constants/enterprise';

import _t from 'i18n!nls/course-cards';

type Props = {
  product: ProductStub;
  recommendedProductIds: Set<string>;
  badgeProductMapping: { [key: string]: EnterpriseBadge };
  selectedBadge?: EnterpriseBadge;
  onEnrollInCredential: (badge: EnterpriseBadge) => void;
};

const ProductBadgeEnrollment = ({
  product,
  recommendedProductIds,
  badgeProductMapping,
  onEnrollInCredential,
}: Props) => {
  const router = useRouter();
  const { loading, error, course: mappedCourse } = useCourseData(product?.productItemId);

  React.useEffect(() => {
    document.location.hash = '';
  }, []);

  if (!product || loading || error || !mappedCourse) return null;

  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const badgeTitle = removeCredentialFromBadgeTitle(badgeProductMapping[product.productItemId].title);

  const redirectToBadge = (e: React.MouseEvent<HTMLAnchorElement>, badgeId: string) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    router.push({
      ...router.location,
      query: {
        ...router.location.query,
        currentTab: TabName.Catalog,
      },
      hash: badgeId,
    });
  };

  return (
    <React.Fragment>
      {recommendedProductIds.has(product.productItemId) && (
        <Grid
          container
          justifyContent="space-between"
          css={{ backgroundColor: 'var(--cds-color-blue-50)', padding: 'var(--cds-spacing-200)' }}
        >
          <Grid item xs={8}>
            <Typography2 component="h3" variant="subtitleMedium">
              {_t("Don't forget to receive your digital badge")}
            </Typography2>
            <Typography2 component="p" variant="bodySecondary">
              <FormattedMessage message={_t('{courseTitle} course is part of the ')} courseTitle={mappedCourse?.name} />
              <Link
                // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                href={`#badge-${badgeProductMapping[product.productItemId].badgeTemplateId}`}
                variant="quiet"
                onClick={(event) =>
                  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                  redirectToBadge(event, `#badge-${badgeProductMapping[product.productItemId].badgeTemplateId}`)
                }
              >
                {badgeTitle}
              </Link>
              <FormattedMessage message={_t(', enroll and receive a digital badge upon successful completion.')} />
            </Typography2>
          </Grid>
          <Grid container xs={4} justifyContent="flex-end" alignItems="center">
            <div>
              <Button
                variant="secondary"
                size="small"
                // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                onClick={() => onEnrollInCredential(badgeProductMapping[product.productItemId])}
                data-testid="enroll-in-credential-button"
              >
                {_t('Enroll in learning track')}
              </Button>
            </div>
          </Grid>
        </Grid>
      )}
    </React.Fragment>
  );
};

export default ProductBadgeEnrollment;
